import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    InputAdornment,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Switch,
    FormControlLabel,
    Slider,
    Tooltip,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, Search as SearchIcon, MyLocation as MyLocationIcon } from '@mui/icons-material';
import { useSettings } from '../contexts/SettingsContext';
import { useAlert } from '../contexts/AlertContext';
import { useGoogleMaps } from './GoogleMapsProvider';
import { carTypesAPI, addOnsAPI } from '../services/apiService';
import {
    getSettingsDashboardCardColors,
    getSettingsMapMarkerColors,
    getSettingsRideStatusColors,
    getSettingsDriverStatusColors,
    getSettingsSoundSettings,
} from '../config/appConstants';

/**
 * CompanySettingsPanel
 * 
 * Admin UI for editing company-wide settings stored in CompanySettings table.
 * All fields are editable. JSON array fields are shown as comma-separated text.
 * 
 * @param {string} [visibleSection] - If provided, only show that section.
 *   One of: 'map', 'pricing-settings', 'wait-time', 'payment', 'scheduling', 'airport', 'locale'.
 *   If omitted, all sections are shown.
 */
// --- Cron helpers ---
const cronToTime = (cron) => {
    try {
        const parts = cron.trim().split(/\s+/);
        if (parts.length >= 2) {
            const minute = parseInt(parts[0], 10);
            const hour = parseInt(parts[1], 10);
            if (!isNaN(minute) && !isNaN(hour)) {
                return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            }
        }
    } catch { /* ignore */ }
    return '07:00';
};

const timeToCron = (time) => {
    const [h, m] = time.split(':').map(Number);
    return `${m} ${h} * * *`;
};

const cronToInterval = (cron) => {
    try {
        const parts = cron.trim().split(/\s+/);
        // e.g. */15 * * * *
        const minMatch = parts[0].match(/^\*\/(\d+)$/);
        if (minMatch) return { value: parseInt(minMatch[1], 10), unit: 'minutes' };
        // e.g. 0 */2 * * *
        const hrMatch = parts[1] && parts[1].match(/^\*\/(\d+)$/);
        if (hrMatch && parts[0] === '0') return { value: parseInt(hrMatch[1], 10), unit: 'hours' };
    } catch { /* ignore */ }
    return { value: 15, unit: 'minutes' };
};

const intervalToCron = (value, unit) => {
    const v = parseInt(value, 10);
    if (!v || v <= 0) return '*/15 * * * *';
    if (unit === 'hours') return `0 */${v} * * *`;
    return `*/${v} * * * *`;
};

// ── Visual color palette options for status dropdowns ──
const PALETTE_OPTIONS = [
    { value: 'default', label: 'Grey', color: '#9e9e9e' },
    { value: 'primary', label: 'Blue', color: '#1976d2' },
    { value: 'secondary', label: 'Purple', color: '#9c27b0' },
    { value: 'error', label: 'Red', color: '#d32f2f' },
    { value: 'warning', label: 'Orange', color: '#ed6c02' },
    { value: 'info', label: 'Light Blue', color: '#0288d1' },
    { value: 'success', label: 'Green', color: '#2e7d32' },
];

// ── Darken a hex color by ~45% (used to auto-compute stroke from fill) ──
const darkenHex = (hex) => {
    if (!hex || !hex.startsWith('#') || hex.length !== 7) return '#000000';
    const factor = 0.55;
    const r = Math.round(parseInt(hex.slice(1, 3), 16) * factor);
    const g = Math.round(parseInt(hex.slice(3, 5), 16) * factor);
    const b = Math.round(parseInt(hex.slice(5, 7), 16) * factor);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// ── Ride status friendly display names ──
const RIDE_STATUS_DISPLAY = {
    canceled: 'Canceled', open: 'Open', assigned: 'Assigned', arriving: 'Arriving',
    atPickup: 'At Pickup', inProgress: 'In Progress', paymentPending: 'Payment Pending',
    droppedOff: 'Dropped Off', completed: 'Completed', scheduled: 'Scheduled',
};

// ── Driver status friendly display names ──
const DRIVER_STATUS_DISPLAY = {
    'No Driver Found': 'No Driver Found', 'Inactive': 'Inactive',
    'Available': 'Available', 'En-Route': 'En-Route', 'Driving': 'Driving',
};

// ── Safe JSON parser with fallback ──
const safeJsonParse = (str, fallback) => {
    if (!str) return fallback;
    try { return JSON.parse(str); }
    catch { return fallback; }
};

// ── Color picker field ──
const ColorPickerField = ({ label, value, onChange, compact }) => (
    <Box display="flex" alignItems="center" gap={1} sx={{ flex: compact ? undefined : 1 }}>
        <Tooltip title={value}>
            <Box
                component="input"
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={{
                    width: 36, height: 36, border: '1px solid',
                    borderColor: 'divider', borderRadius: 1,
                    cursor: 'pointer', p: 0.25,
                    '&::-webkit-color-swatch-wrapper': { p: 0 },
                    '&::-webkit-color-swatch': { borderRadius: 0.5, border: 'none' },
                }}
            />
        </Tooltip>
        {!compact && <Typography variant="body2">{label}</Typography>}
        {compact && <Typography variant="caption" color="text.secondary">{label}</Typography>}
        <TextField
            size="small" value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{ width: compact ? 100 : 120 }}
            inputProps={{ style: { fontSize: '0.8rem', fontFamily: 'monospace' } }}
        />
    </Box>
);

// ── Visual palette selector for MUI status colors ──
const PaletteColorSelector = ({ value, onChange }) => (
    <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
        {PALETTE_OPTIONS.map(opt => (
            <Tooltip key={opt.value} title={opt.label}>
                <Box
                    onClick={() => onChange(opt.value)}
                    sx={{
                        width: 28, height: 28,
                        bgcolor: opt.color,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        border: value === opt.value ? '3px solid' : '2px solid',
                        borderColor: value === opt.value ? 'text.primary' : 'transparent',
                        boxSizing: 'border-box',
                        outline: value === opt.value ? '2px solid white' : 'none',
                        outlineOffset: '-4px',
                        transition: 'transform 0.1s',
                        '&:hover': { transform: 'scale(1.2)' },
                    }}
                />
            </Tooltip>
        ))}
    </Box>
);

const CompanySettingsPanel = ({ visibleSection, onDirtyChange }) => {
    const { settings, loading, error, updateSettings, fetchSettings } = useSettings();
    const { showToast } = useAlert();
    const { isLoaded: mapsLoaded } = useGoogleMaps();
    const { addOns, refreshAddOns } = useSettings();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [carTypes, setCarTypes] = useState([]);
    const [addOnsList, setAddOnsList] = useState([]);
    const [newAddOn, setNewAddOn] = useState({ name: '', fee: '' });
    const [editingAddOn, setEditingAddOn] = useState(null); // { id, name, fee }

    // Sync local addOns list from context
    useEffect(() => { setAddOnsList(addOns); }, [addOns]);

    // Notify parent of dirty state changes
    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);

    // Clear dirty state when unmounting so the parent stops guarding navigation
    useEffect(() => {
        return () => onDirtyChange?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Block browser refresh / tab close when there are unsaved changes
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // Fetch car types for premium wait time dropdown
    useEffect(() => {
        carTypesAPI.getAll().then(data => {
            setCarTypes((data || []).sort((a, b) => a.sortOrder - b.sortOrder));
        }).catch(err => console.error('Error fetching car types:', err));
    }, []);

    // Map location search state
    const [locationQuery, setLocationQuery] = useState('');
    const [locationName, setLocationName] = useState('');
    const [geocoding, setGeocoding] = useState(false);
    const geocoderRef = useRef(null);

    // Scheduling friendly state
    const [distributionTime, setDistributionTime] = useState('07:00');
    const [reminderValue, setReminderValue] = useState(15);
    const [reminderUnit, setReminderUnit] = useState('minutes');

    // Populate form when settings load
    useEffect(() => {
        if (settings) {
            setForm({
                ...settings,
                // Convert JSON arrays to comma-separated strings for editing
                autocompleteCountries: jsonToComma(settings.autocompleteCountries),
                premiumWaitTimeCarTypes: jsonToComma(settings.premiumWaitTimeCarTypes),
                // Re-serialize JSON color/sound settings merged with defaults so any
                // keys missing from the saved record are filled in. Otherwise editing
                // a single color and saving would drop the rest of the keys.
                dashboardCardColors: JSON.stringify(getSettingsDashboardCardColors(settings)),
                mapMarkerColors: JSON.stringify(getSettingsMapMarkerColors(settings)),
                rideStatusColors: JSON.stringify(getSettingsRideStatusColors(settings)),
                driverStatusColors: JSON.stringify(getSettingsDriverStatusColors(settings)),
                soundSettings: JSON.stringify(getSettingsSoundSettings(settings)),
            });
            setDirty(false);

            // Initialize scheduling friendly values from cron
            if (settings.scheduledDistributionCron) {
                setDistributionTime(cronToTime(settings.scheduledDistributionCron));
            }
            if (settings.reminderCheckCron) {
                const interval = cronToInterval(settings.reminderCheckCron);
                setReminderValue(interval.value);
                setReminderUnit(interval.unit);
            }

        }
    }, [settings]);

    // Initialize geocoder when Maps API loads, then reverse-geocode saved coordinates
    useEffect(() => {
        if (!mapsLoaded || !window.google?.maps?.Geocoder) return;
        geocoderRef.current = new window.google.maps.Geocoder();
        if (settings?.mapCenterLat && settings?.mapCenterLng) {
            reverseGeocode(Number(settings.mapCenterLat), Number(settings.mapCenterLng));
        }
    }, [mapsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reverse-geocode lat/lng to a readable name
    const reverseGeocode = useCallback((lat, lng) => {
        if (!geocoderRef.current) {
            if (!window.google?.maps?.Geocoder) return;
            geocoderRef.current = new window.google.maps.Geocoder();
        }
        geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                setLocationName(results[0].formatted_address);
                setLocationQuery(results[0].formatted_address);
            }
        });
    }, []);

    // Forward-geocode a city/zip to lat/lng
    const handleLocationSearch = useCallback(() => {
        if (!locationQuery.trim()) return;
        if (!geocoderRef.current) {
            if (!window.google?.maps?.Geocoder) {
                showToast('Google Maps not loaded yet', 'warning');
                return;
            }
            geocoderRef.current = new window.google.maps.Geocoder();
        }
        setGeocoding(true);
        geocoderRef.current.geocode({ address: locationQuery.trim() }, (results, status) => {
            setGeocoding(false);
            if (status === 'OK' && results[0]) {
                const loc = results[0].geometry.location;
                const lat = loc.lat();
                const lng = loc.lng();
                setForm(prev => ({ ...prev, mapCenterLat: lat, mapCenterLng: lng }));
                setLocationName(results[0].formatted_address);
                setLocationQuery(results[0].formatted_address);
                setDirty(true);
            } else {
                showToast('Location not found. Try a different city or zip code.', 'warning');
            }
        });
    }, [locationQuery, showToast]);

    const jsonToComma = (jsonStr) => {
        try {
            return JSON.parse(jsonStr).join(', ');
        } catch {
            return jsonStr || '';
        }
    };

    const commaToJson = (commaStr) => {
        const items = commaStr.split(',').map(s => s.trim()).filter(Boolean);
        // Try to parse as numbers if all items are numeric
        const parsed = items.map(item => {
            const num = Number(item);
            return isNaN(num) ? item : num;
        });
        return JSON.stringify(parsed);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Safe numeric conversion: if a field is blank/NaN, fall back to the currently
            // loaded settings value rather than sending 0/null. The server does a full-row
            // overwrite, so a NaN→null here would silently wipe that column to 0.
            const num = (field) => {
                const n = Number(form[field]);
                if (!Number.isNaN(n)) return n;
                const existing = Number(settings?.[field]);
                return Number.isNaN(existing) ? 0 : existing;
            };
            const payload = {
                ...form,
                id: settings.id || 1,
                // Convert comma-separated strings back to JSON
                autocompleteCountries: commaToJson(form.autocompleteCountries),
                premiumWaitTimeCarTypes: commaToJson(form.premiumWaitTimeCarTypes),
                // Convert friendly scheduling values to cron
                scheduledDistributionCron: timeToCron(distributionTime),
                reminderCheckCron: intervalToCron(reminderValue, reminderUnit),
                // Ensure numeric types (num() falls back to the loaded value instead of NaN/0)
                mapCenterLat: num('mapCenterLat'),
                mapCenterLng: num('mapCenterLng'),
                mapDefaultZoom: num('mapDefaultZoom'),
                minimumFare: num('minimumFare'),
                minStopCost: num('minStopCost'),
                stopDriverComp: num('stopDriverComp'),
                stopCompanySharePercent: num('stopCompanySharePercent'),
                stopPerMileRate: num('stopPerMileRate'),
                stopDistanceThresholdMiles: 3,
                formulaCompanySharePercent: num('formulaCompanySharePercent'),
                roundTripMultiplier: num('roundTripMultiplier'),
                defaultPerMileRate: num('defaultPerMileRate'),
                defaultMinPerHourRate: num('defaultMinPerHourRate'),
                freeWaitPickupMinutes: num('freeWaitPickupMinutes'),
                freeWaitStopMinutes: num('freeWaitStopMinutes'),
                waitTimeRateStandard: num('waitTimeRateStandard'),
                waitTimeRatePremium: num('waitTimeRatePremium'),
                driverWaitTimeSharePercent: num('driverWaitTimeSharePercent'),
                rideVisibilityDays: num('rideVisibilityDays'),
                rideReminderMinutes: num('rideReminderMinutes'),
                upcomingRideThresholdMinutes: num('upcomingRideThresholdMinutes'),
                interRideBufferMinutes: num('interRideBufferMinutes'),
                maxStops: num('maxStops'),
                heartbeatTimeoutMinutes: num('heartbeatTimeoutMinutes'),
                use24HourTime: Boolean(form.use24HourTime),
                // Display / UI
                defaultPageSize: num('defaultPageSize'),
            };

            await updateSettings(payload);
            showToast('Settings saved successfully', 'success');
            setDirty(false);
        } catch (err) {
            console.error('Failed to save settings:', err);
            showToast('Failed to save settings', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !settings) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Failed to load company settings. Please try again later.
            </Alert>
        );
    }

    const showAll = !visibleSection;
    const show = (id) => showAll || visibleSection === id;

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                {showAll && <Typography variant="h5">Company Settings</Typography>}
                <Box display="flex" gap={1} ml="auto">
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => {
                            if (dirty && !window.confirm('You have unsaved changes. Discard them and refresh?')) return;
                            fetchSettings();
                        }}
                        disabled={saving}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || !dirty}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {dirty && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You have unsaved changes.
                </Alert>
            )}

            {/* Pricing */}
            {show('pricing-settings') && (
                <>
                    {showAll && <SectionHeader title="Pricing" />}
                    <Grid container spacing={2} sx={{ mb: 3 }} direction="column">
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Minimum Fare ($)" type="number"
                                value={form.minimumFare ?? ''} onChange={e => handleChange('minimumFare', e.target.value)}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Min Stop Cost ($)" type="number"
                                value={form.minStopCost ?? ''} onChange={e => handleChange('minStopCost', e.target.value)}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Stop Driver Comp ($)" type="number"
                                value={form.stopDriverComp ?? ''} onChange={e => handleChange('stopDriverComp', e.target.value)}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Stop Company Share (%)" type="number"
                                value={form.stopCompanySharePercent ?? ''} onChange={e => handleChange('stopCompanySharePercent', e.target.value)}
                                helperText="Decimal e.g. 0.30 = 30%"
                                inputProps={{ step: '0.01', min: 0, max: 1 }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Stop Per-Mile Rate ($/mi)" type="number"
                                value={form.stopPerMileRate ?? ''} onChange={e => handleChange('stopPerMileRate', e.target.value)}
                                helperText="Per-mile rate applied when a stop exceeds the distance threshold"
                                inputProps={{ step: '0.50', min: 0 }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Default Min Per-Hour Rate ($/hr)" type="number"
                                value={form.defaultMinPerHourRate ?? ''} onChange={e => handleChange('defaultMinPerHourRate', e.target.value)}
                                helperText="Formula pricing: if hourly cost exceeds mileage cost for trips > 60 min, use hourly cost"
                                inputProps={{ step: '1', min: 0 }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Formula Company Share (%)" type="number"
                                value={form.formulaCompanySharePercent ?? ''} onChange={e => handleChange('formulaCompanySharePercent', e.target.value)}
                                helperText="e.g. 0.30 = 30% company, 70% driver"
                                inputProps={{ step: '0.01', min: 0, max: 1 }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Round Trip Multiplier" type="number"
                                value={form.roundTripMultiplier ?? ''} onChange={e => handleChange('roundTripMultiplier', e.target.value)}
                                helperText="Multiplied by one-way fare for round trip (e.g. 1.5 = 150%)"
                                inputProps={{ step: '0.1', min: 1 }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth label="Default Per-Mile Rate ($)" type="number"
                                value={form.defaultPerMileRate ?? ''} onChange={e => handleChange('defaultPerMileRate', e.target.value)}
                                helperText="Fallback rate when no formula setting exists for a car type"
                                inputProps={{ step: '0.01', min: 0 }}
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Add-Ons */}
            {
                show('add-ons') && (
                    <>
                        {showAll && <SectionHeader title="Add-Ons" />}
                        <Box sx={{ mb: 3 }}>
                            {addOnsList.map(addOn => (
                                <Box key={addOn.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    {editingAddOn?.id === addOn.id ? (
                                        <>
                                            <TextField size="small" label="Name" value={editingAddOn.name}
                                                onChange={e => setEditingAddOn(prev => ({ ...prev, name: e.target.value }))} />
                                            <TextField size="small" label="Fee ($)" type="number" value={editingAddOn.fee} sx={{ width: 100 }}
                                                onChange={e => setEditingAddOn(prev => ({ ...prev, fee: e.target.value }))} />
                                            <Button size="small" variant="contained" onClick={async () => {
                                                await addOnsAPI.update(editingAddOn.id, { name: editingAddOn.name, fee: Number(editingAddOn.fee) });
                                                setEditingAddOn(null);
                                                refreshAddOns();
                                            }}>Save</Button>
                                            <Button size="small" onClick={() => setEditingAddOn(null)}>Cancel</Button>
                                        </>
                                    ) : (
                                        <>
                                            <Typography sx={{ flex: 1 }}>{addOn.name} — ${Number(addOn.fee).toFixed(2)}</Typography>
                                            {!addOn.isActive && <Chip size="small" label="Inactive" color="default" />}
                                            <Button size="small" onClick={() => setEditingAddOn({ id: addOn.id, name: addOn.name, fee: addOn.fee })}>Edit</Button>
                                            {addOn.isActive
                                                ? <Button size="small" color="error" onClick={async () => { await addOnsAPI.delete(addOn.id); refreshAddOns(); }}>Deactivate</Button>
                                                : <Button size="small" color="success" onClick={async () => { await addOnsAPI.reactivate(addOn.id); refreshAddOns(); }}>Reactivate</Button>
                                            }
                                        </>
                                    )}
                                </Box>
                            ))}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField size="small" label="Name" value={newAddOn.name}
                                    onChange={e => setNewAddOn(prev => ({ ...prev, name: e.target.value }))} />
                                <TextField size="small" label="Fee ($)" type="number" value={newAddOn.fee} sx={{ width: 100 }}
                                    onChange={e => setNewAddOn(prev => ({ ...prev, fee: e.target.value }))} />
                                <Button variant="outlined" size="small" onClick={async () => {
                                    if (!newAddOn.name || newAddOn.fee === '') return;
                                    await addOnsAPI.create({ name: newAddOn.name, fee: Number(newAddOn.fee) });
                                    setNewAddOn({ name: '', fee: '' });
                                    refreshAddOns();
                                }}>Add Add-On</Button>
                            </Box>
                        </Box>
                    </>
                )
            }

            {/* Wait Time */}
            {
                show('wait-time') && (
                    <>
                        {showAll && <SectionHeader title="Wait Time" />}
                        <Grid container spacing={2} sx={{ mb: 3 }} direction="column">
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Free Wait at Pickup (min)" type="number"
                                    value={form.freeWaitPickupMinutes ?? ''} onChange={e => handleChange('freeWaitPickupMinutes', e.target.value)}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Free Wait at Stops (min)" type="number"
                                    value={form.freeWaitStopMinutes ?? ''} onChange={e => handleChange('freeWaitStopMinutes', e.target.value)}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Driver Wait Time Share (%)" type="number"
                                    value={form.driverWaitTimeSharePercent ?? ''} onChange={e => handleChange('driverWaitTimeSharePercent', e.target.value)}
                                    helperText="e.g. 0.85 = 85%"
                                    inputProps={{ step: '0.01', min: 0, max: 1 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Standard Rate ($/min)" type="number"
                                    value={form.waitTimeRateStandard ?? ''} onChange={e => handleChange('waitTimeRateStandard', e.target.value)}
                                    inputProps={{ step: '0.01' }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Premium Rate ($/min)" type="number"
                                    value={form.waitTimeRatePremium ?? ''} onChange={e => handleChange('waitTimeRatePremium', e.target.value)}
                                    inputProps={{ step: '0.01' }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Premium Car Types</InputLabel>
                                    <Select
                                        multiple
                                        value={(() => {
                                            try {
                                                const val = form.premiumWaitTimeCarTypes;
                                                if (!val) return [];
                                                if (typeof val === 'string') {
                                                    const ids = val.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
                                                    return ids;
                                                }
                                                return Array.isArray(val) ? val : [];
                                            } catch { return []; }
                                        })()}
                                        onChange={(e) => {
                                            handleChange('premiumWaitTimeCarTypes', e.target.value.join(','));
                                        }}
                                        input={<OutlinedInput label="Premium Car Types" />}
                                        renderValue={(selected) => selected.map(id => carTypes.find(ct => ct.id === id)?.name || id).join(', ')}
                                    >
                                        {carTypes.map(ct => (
                                            <MenuItem key={ct.id} value={ct.id}>
                                                <Checkbox checked={(() => {
                                                    try {
                                                        const val = form.premiumWaitTimeCarTypes;
                                                        if (!val) return false;
                                                        const ids = typeof val === 'string' ? val.split(',').map(s => parseInt(s.trim())) : val;
                                                        return ids.includes(ct.id);
                                                    } catch { return false; }
                                                })()} />
                                                <ListItemText primary={ct.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </>
                )
            }

            {/* Scheduling / Operations */}
            {
                show('scheduling') && (
                    <>
                        {showAll && <SectionHeader title="Scheduling & Operations" />}
                        <Grid container spacing={2} sx={{ mb: 3 }} direction="column">
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Ride Visibility (days)" type="number"
                                    value={form.rideVisibilityDays ?? ''} onChange={e => handleChange('rideVisibilityDays', e.target.value)}
                                    helperText="How many days ahead drivers can see upcoming rides"
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Ride Reminder (min before)" type="number"
                                    value={form.rideReminderMinutes ?? ''} onChange={e => handleChange('rideReminderMinutes', e.target.value)}
                                    helperText="How many minutes before a ride the driver receives a push notification reminder"
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Upcoming Ride Threshold (min)" type="number"
                                    value={form.upcomingRideThresholdMinutes ?? ''} onChange={e => handleChange('upcomingRideThresholdMinutes', e.target.value)}
                                    helperText="If a driver has a ride starting within this many minutes, heartbeat-loss alerts are suppressed (they are likely driving and just offline)"
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Inter-Ride Buffer (min)" type="number"
                                    value={form.interRideBufferMinutes ?? 30} onChange={e => handleChange('interRideBufferMinutes', e.target.value)}
                                    helperText="Extra buffer added after a ride’s estimated end time before a driver is considered available for the next ride (e.g. 30 min means a ride ending at 4:00 leaves the driver unavailable until 4:30)"
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                        </Grid>
                    </>
                )
            }

            {/* Airport Detection */}
            {
                show('airport') && (
                    <>
                        {showAll && <SectionHeader title="Airport Detection" />}
                        <Grid container spacing={2} sx={{ mb: 3 }} direction="column">
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Airport Detection Rules (JSON)"
                                    value={form.airportDetectionRules ?? ''}
                                    onChange={e => handleChange('airportDetectionRules', e.target.value)}
                                    multiline
                                    minRows={3}
                                    maxRows={8}
                                    helperText='JSON array, e.g. [{"keywords":["jfk","kennedy airport"],"zip":"11430"}, ...]'
                                />
                            </Grid>
                        </Grid>
                    </>
                )
            }

            {/* Timezone & Locale */}
            {
                show('locale') && (
                    <>
                        {showAll && <SectionHeader title="Timezone & Locale" />}
                        <Grid container spacing={2} sx={{ mb: 3 }} direction="column">
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Timezone"
                                    value={form.timezone ?? ''} onChange={e => handleChange('timezone', e.target.value)}
                                    helperText='Windows timezone ID, e.g. "Eastern Standard Time" or IANA "America/New_York"'
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Locale"
                                    value={form.locale ?? ''} onChange={e => handleChange('locale', e.target.value)}
                                    helperText='Date/number formatting locale, e.g. "en-US"'
                                />
                            </Grid>
                            <Grid size={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(form.use24HourTime)}
                                            onChange={e => handleChange('use24HourTime', e.target.checked)}
                                        />
                                    }
                                    label="Use 24-hour time format"
                                />
                            </Grid>
                        </Grid>
                    </>
                )
            }

            {/* Display & Colors (includes Map Settings) */}
            {
                (show('display') || show('map')) && (
                    <>
                        {showAll && <SectionHeader title="Display & Colors" />}
                        <Grid container spacing={2} sx={{ mb: 3 }} direction="column">

                            {/* History Tab */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>History Tab</Typography>
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Default Page Size" type="number"
                                    value={form.defaultPageSize ?? 50} onChange={e => handleChange('defaultPageSize', e.target.value)}
                                    helperText="Number of rows shown per page in History"
                                    inputProps={{ min: 10, max: 200 }}
                                />
                            </Grid>

                            {/* Dashboard Card Colors */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Dashboard Metric Card Colors</Typography>
                            </Grid>
                            {(() => {
                                const defaults = {
                                    recurringThisWeek: '#0288d1', todaysRecurring: '#0097a7', todaysRides: '#2e7d32', futureRides: '#607d8b',
                                    openRides: '#ed6c02', assignedRides: '#1976d2', ridesInProgress: '#d32f2f',
                                    completedRides: '#388e3c', activeDrivers: '#9c27b0', driversOnJob: '#7b1fa2',
                                    unsettledDrivers: '#ff9800', offlineDrivers: '#757575',
                                };
                                const parsed = safeJsonParse(form.dashboardCardColors, defaults);
                                const labels = {
                                    recurringThisWeek: 'Recurring This Week', todaysRecurring: "Today's Recurring",
                                    todaysRides: "Today's Rides", futureRides: 'Future Rides', openRides: 'Open Rides',
                                    assignedRides: 'Assigned Rides', ridesInProgress: 'Rides in Progress',
                                    completedRides: 'Completed Rides (Unreviewed)', activeDrivers: 'Active Drivers',
                                    driversOnJob: 'Drivers on Job', unsettledDrivers: 'Unsettled Drivers',
                                    offlineDrivers: 'Offline Drivers',
                                };
                                return Object.entries(labels)
                                    .filter(([key]) => key !== 'unsettledDrivers' || form.showReportsTab)
                                    .filter(([key]) => (key !== 'recurringThisWeek' && key !== 'todaysRecurring') || form.showRecurringCallsTab)
                                    .map(([key, label]) => (
                                        <Grid size={12} key={key}>
                                            <ColorPickerField
                                                label={label}
                                                value={parsed[key] || defaults[key] || '#000000'}
                                                onChange={(color) => {
                                                    const updated = { ...parsed, [key]: color };
                                                    handleChange('dashboardCardColors', JSON.stringify(updated));
                                                }}
                                            />
                                        </Grid>
                                    ));
                            })()}

                            {/* Ride Status Colors */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Ride Status Colors</Typography>
                                <Typography variant="caption" color="text.secondary">Click a color to assign it to each ride status</Typography>
                            </Grid>
                            {(() => {
                                const parsed = safeJsonParse(form.rideStatusColors, {});
                                return Object.entries(parsed).map(([key, value]) => (
                                    <Grid size={12} key={key}>
                                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                            <Typography sx={{ minWidth: 160, fontSize: '0.875rem' }}>
                                                {RIDE_STATUS_DISPLAY[key] || key}
                                            </Typography>
                                            <PaletteColorSelector
                                                value={value}
                                                onChange={(newVal) => {
                                                    const updated = { ...parsed, [key]: newVal };
                                                    handleChange('rideStatusColors', JSON.stringify(updated));
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                ));
                            })()}

                            {/* Driver Status Colors */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Driver Status Colors</Typography>
                                <Typography variant="caption" color="text.secondary">Click a color to assign it to each driver status</Typography>
                            </Grid>
                            {(() => {
                                const parsed = safeJsonParse(form.driverStatusColors, {});
                                return Object.entries(parsed).map(([key, value]) => (
                                    <Grid size={12} key={key}>
                                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                                            <Typography sx={{ minWidth: 160, fontSize: '0.875rem' }}>
                                                {DRIVER_STATUS_DISPLAY[key] || key}
                                            </Typography>
                                            <PaletteColorSelector
                                                value={value}
                                                onChange={(newVal) => {
                                                    const updated = { ...parsed, [key]: newVal };
                                                    handleChange('driverStatusColors', JSON.stringify(updated));
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                ));
                            })()}

                            {/* Map Settings (merged in) */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Map Settings</Typography>
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Map Center — City or Zip Code"
                                    value={locationQuery}
                                    onChange={e => setLocationQuery(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleLocationSearch(); } }}
                                    placeholder="e.g. Lakewood, NJ or 08701"
                                    helperText={locationName ? `Current: ${locationName}` : 'Enter a city or zip code and click search'}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleLocationSearch} disabled={geocoding || !locationQuery.trim()} edge="end">
                                                        {geocoding ? <CircularProgress size={20} /> : <SearchIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }
                                    }}
                                />
                            </Grid>
                            {locationName && (
                                <Grid size={12}>
                                    <Alert severity="success" icon={<MyLocationIcon />} sx={{ py: 0.5 }}>
                                        Map will center on: <strong>{locationName}</strong>
                                    </Alert>
                                </Grid>
                            )}
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Default Zoom" type="number"
                                    value={form.mapDefaultZoom ?? ''} onChange={e => handleChange('mapDefaultZoom', e.target.value)}
                                    inputProps={{ min: 1, max: 20 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth label="Autocomplete Countries (comma-separated)"
                                    value={form.autocompleteCountries ?? ''} onChange={e => handleChange('autocompleteCountries', e.target.value)}
                                    helperText="e.g. us, ca"
                                />
                            </Grid>

                            {/* Map Marker Colors */}
                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Map Marker Colors — Drivers</Typography>
                                <Typography variant="caption" color="text.secondary">Choose the color</Typography>
                            </Grid>
                            {(() => {
                                const parsed = safeJsonParse(form.mapMarkerColors, {});
                                const drivers = parsed.driver || {};
                                const driverLabels = { driving: 'Driving', drivingOffline: 'Driving (Offline)', available: 'Available', upcomingRide: 'Upcoming Ride', highlighted: 'Highlighted' };
                                return Object.entries(driverLabels).map(([key, label]) => (
                                    <Grid size={12} key={`driver-${key}`}>
                                        <ColorPickerField
                                            label={label}
                                            value={drivers[key]?.fill || '#000000'}
                                            onChange={(color) => {
                                                const stroke = darkenHex(color);
                                                const updated = { ...parsed, driver: { ...drivers, [key]: { fill: color, stroke } } };
                                                handleChange('mapMarkerColors', JSON.stringify(updated));
                                            }}
                                        />
                                    </Grid>
                                ));
                            })()}

                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Map Marker Colors — Rides</Typography>
                                <Typography variant="caption" color="text.secondary">Choose the color</Typography>
                            </Grid>
                            {(() => {
                                const parsed = safeJsonParse(form.mapMarkerColors, {});
                                const rides = parsed.ride || {};
                                const rideLabels = { missed: 'Missed', inProgress: 'In Progress', future: 'Future', completed: 'Completed', atPickup: 'At Pickup', droppedOff: 'Dropped Off' };
                                return Object.entries(rideLabels).map(([key, label]) => (
                                    <Grid size={12} key={`ride-${key}`}>
                                        <ColorPickerField
                                            label={label}
                                            value={rides[key]?.fill || '#000000'}
                                            onChange={(color) => {
                                                const stroke = darkenHex(color);
                                                const updated = { ...parsed, ride: { ...rides, [key]: { fill: color, stroke } } };
                                                handleChange('mapMarkerColors', JSON.stringify(updated));
                                            }}
                                        />
                                    </Grid>
                                ));
                            })()}
                        </Grid>
                    </>
                )
            }

            {/* Driver Application Form */}
            {
                show('driver-application') && (
                    <>
                        {showAll && <SectionHeader title="Driver Application Form" />}

                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Invite Link Token Lifetime (days)"
                                    type="number"
                                    value={form.applicationTokenLifetimeDays ?? 7}
                                    onChange={e => handleChange('applicationTokenLifetimeDays', Number(e.target.value))}
                                    inputProps={{ min: 1, max: 90 }}
                                    helperText="How many days an invite link remains valid before expiring"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Notification Email"
                                    type="email"
                                    value={form.applicationNotificationEmail ?? ''}
                                    onChange={e => handleChange('applicationNotificationEmail', e.target.value)}
                                    helperText="Email address to notify when a new application is submitted (optional)"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Intro / Welcome Text"
                                    multiline
                                    minRows={3}
                                    value={form.applicationFormIntroText ?? ''}
                                    onChange={e => handleChange('applicationFormIntroText', e.target.value)}
                                    helperText="Optional text shown at the top of the driver application form"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    label="Company Logo URL"
                                    value={form.applicationFormCompanyLogo ?? ''}
                                    onChange={e => handleChange('applicationFormCompanyLogo', e.target.value)}
                                    helperText="Optional URL to display your logo on the application form"
                                />
                            </Grid>

                            <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                    Enabled Form Fields
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    Choose which fields applicants must fill out
                                </Typography>
                                {[
                                    {
                                        group: 'Personal', fields: [
                                            { key: 'dateOfBirth', label: 'Date of Birth' },
                                            { key: 'gender', label: 'Gender' },
                                            { key: 'ssn', label: 'Social Security Number' },
                                        ]
                                    },
                                    {
                                        group: 'Address', fields: [
                                            { key: 'addressStreet', label: 'Street' },
                                            { key: 'addressCity', label: 'City' },
                                            { key: 'addressState', label: 'State' },
                                            { key: 'addressZip', label: 'Zip Code' },
                                        ]
                                    },
                                    {
                                        group: 'Emergency Contact', fields: [
                                            { key: 'emergencyContactName', label: 'Name' },
                                            { key: 'emergencyContactPhone', label: 'Phone' },
                                        ]
                                    },
                                    {
                                        group: "Driver's License", fields: [
                                            { key: 'licenseNumber', label: 'License Number' },
                                            { key: 'licenseState', label: 'License State' },
                                            { key: 'licenseExpiration', label: 'Expiration Date' },
                                            { key: 'chauffeurLicenseNumber', label: 'Chauffeur License Number' },
                                        ]
                                    },
                                    {
                                        group: 'Insurance', fields: [
                                            { key: 'insuranceCarrier', label: 'Carrier' },
                                            { key: 'insurancePolicyNumber', label: 'Policy Number' },
                                            { key: 'insuranceExpiration', label: 'Expiration Date' },
                                        ]
                                    },
                                    {
                                        group: 'Other', fields: [
                                            { key: 'yearsDrivingExperience', label: 'Years of Experience' },
                                            { key: 'languagesSpoken', label: 'Languages Spoken' },
                                            { key: 'preferredWorkHours', label: 'Preferred Work Hours' },
                                            { key: 'backgroundCheckConsent', label: 'Background Check Consent' },
                                        ]
                                    },
                                    {
                                        group: 'Documents', fields: [
                                            { key: 'profilePhoto', label: 'Profile Photo' },
                                            { key: 'licensePhoto', label: 'License Photo' },
                                            { key: 'insurancePhoto', label: 'Insurance Card Photo' },
                                            { key: 'chauffeurLicensePhoto', label: 'Chauffeur License Photo' },
                                        ]
                                    },
                                    {
                                        group: 'Vehicle', fields: [
                                            { key: 'car', label: 'Car (Type / Make / Model / Year / Color / License Plate / Seats / Photo)' },
                                        ]
                                    },
                                ].map(({ group, fields }) => {
                                    let enabledFields = [];
                                    try { enabledFields = JSON.parse(form.applicationFormEnabledFields || '[]'); } catch { }
                                    return (
                                        <Box key={group} mb={1.5}>
                                            <Typography variant="caption" fontWeight={600} color="text.secondary">{group}</Typography>
                                            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                                {fields.map(({ key, label }) => {
                                                    const checked = enabledFields.includes(key);
                                                    return (
                                                        <Box
                                                            key={key}
                                                            onClick={() => {
                                                                let next;
                                                                try { next = JSON.parse(form.applicationFormEnabledFields || '[]'); } catch { next = []; }
                                                                if (checked) next = next.filter(f => f !== key);
                                                                else next = [...next, key];
                                                                handleChange('applicationFormEnabledFields', JSON.stringify(next));
                                                            }}
                                                            sx={{
                                                                px: 1.5, py: 0.5,
                                                                border: '1px solid',
                                                                borderColor: checked ? 'primary.main' : 'divider',
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                bgcolor: checked ? 'primary.main' : 'transparent',
                                                                color: checked ? 'primary.contrastText' : 'text.primary',
                                                                fontSize: '0.8rem',
                                                                userSelect: 'none',
                                                                transition: 'all 0.15s',
                                                            }}
                                                        >
                                                            {label}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </>
                )
            }

        </Box >
    );
};

const SectionHeader = ({ title }) => (
    <Box mb={1} mt={2}>
        <Typography variant="h6" color="primary" gutterBottom>{title}</Typography>
        <Divider />
    </Box>
);

export default CompanySettingsPanel;
